/* eslint-disable @typescript-eslint/indent */
import * as React from 'react';
import styled from 'styled-components';
import { themes } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';
import { ConsentDecision } from '../';
import AdditionalOptionQuestion from '../Components/AdditionalOptionQuestion';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import Loader from 'react-loading';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
  padding-right: 20px;

  @media screen and (min-width: ${themes.breakpoints.tabletLandscape}) {
    margin-top: 24px;
  }
`;

const TitleText = styled.span`
  ${themes.fonts.DomaineDisplay};
  font-size: 28px;
  color: ${themes.colors.KBNight};

  @media screen and (min-width: ${themes.breakpoints.tabletLandscape}) {
    font-size: 36px;
  }
`;

const SubtitleText = styled.span`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  color: ${themes.colors.KBNight};
  white-space: pre-wrap;
`;

const QuestionsContainer = styled.div`
  padding-top: 28px;
`;

const OptionContainer = styled.div`
  margin: 18px 0px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

interface CreateConsentDecisionResponseData {
  createPatientConsentDecision: {
    id: string;
  };
}

const CREATE_CONSENT_DECISION = gql`
  mutation createPatientConsentDecision(
    $consentOptionId: Int!
    $patientConsentId: Int!
    $consentDecisionId: Int!
  ) {
    createPatientConsentDecision(
      consentOptionId: $consentOptionId
      patientConsentId: $patientConsentId
      consentDecisionId: $consentDecisionId
    ) {
      patientConsentDecision {
        id
      }
    }
  }
`;

type Props = {
  consentDecisions: ConsentDecision[];
  patientConsentId: string;
  onCompleted: () => void;
};

type State = {
  currentDecision: number;
  loading: boolean;
};

export default class AdditionalOptions extends React.Component<Props, State> {
  state = {
    currentDecision: 0,
    loading: false
  };

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  getCurrentDecision(): ConsentDecision {
    return this.props.consentDecisions[this.state.currentDecision];
  }

  renderLoader() {
    return (
      <LoadingContainer>
        <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
      </LoadingContainer>
    );
  }

  renderQuestions() {
    if (this.state.loading) {
      return this.renderLoader();
    }
    const currentDecision = this.getCurrentDecision();
    if (!currentDecision) return null;
    return (
      <QuestionsContainer>
        {currentDecision.consentOptions.map(option => {
          return (
            <Mutation<
              CreateConsentDecisionResponseData,
              {
                patientConsentId: number;
                consentOptionId: number;
                consentDecisionId: string;
              }
            >
              key={option.id}
              mutation={CREATE_CONSENT_DECISION}
            >
              {submitDecision => {
                return (
                  <OptionContainer>
                    <AdditionalOptionQuestion
                      option={option}
                      onSelect={() => {
                        this.setState({
                          loading: true
                        });
                        submitDecision({
                          variables: {
                            patientConsentId: parseInt(this.props.patientConsentId),
                            consentOptionId: parseInt(option.id),
                            consentDecisionId: option.consentDecisionId
                          }
                        })
                          .then(() => {
                            if (
                              this.state.currentDecision ===
                              this.props.consentDecisions.length - 1
                            ) {
                              this.props.onCompleted();
                            } else {
                              this.setState({
                                currentDecision: this.state.currentDecision + 1
                              });
                            }
                            this.setState({
                              loading: false
                            });
                          })
                          .catch(error => Honeybadger.notify(error));
                      }}
                    />
                  </OptionContainer>
                );
              }}
            </Mutation>
          );
        })}
      </QuestionsContainer>
    );
  }

  render() {
    if (this.state.loading) {
      return this.renderLoader();
    }
    const currentDecision = this.getCurrentDecision();
    return (
      <Container>
        <TitleText>{currentDecision.name}</TitleText>
        {currentDecision && <SubtitleText>{currentDecision.description}</SubtitleText>}
        {this.renderQuestions()}
      </Container>
    );
  }
}
